import React, { useState, useEffect } from "react";
import { useRepairContext } from "@/context/RepairContext";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Search, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RepairItem } from "@/types";

const PaymentConfirmations: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { repairs, updateRepair, loading } = useRepairContext();
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // State for PIN authentication (required every time page is accessed)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(true);
  const [pinCode, setPinCode] = useState("");
  const [pinError, setPinError] = useState("");

  // State for payment confirmation dialog (simple confirmation after PIN)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState<RepairItem | null>(null);

  // Filter repairs that are paid but not confirmed
  const needConfirmation = repairs.filter(
    (repair) => repair.paymentStatus === "paid" && !repair.paymentConfirmed
  );

  // Filter by search term if provided
  const filteredRepairs = searchTerm
    ? needConfirmation.filter(
        (repair) =>
          repair.customerName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          repair.customerPhone.includes(searchTerm) ||
          repair.phoneModel.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (repair.ticketNumber?.toString() || "").includes(searchTerm)
      )
    : needConfirmation;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  // Reset authentication state when component mounts (always require PIN)
  useEffect(() => {
    // Always start with authentication required
    setIsAuthenticated(false);
    setPinDialogOpen(true);
    setPinCode("");
    setPinError("");

    // Cleanup function to reset authentication when component unmounts
    return () => {
      setIsAuthenticated(false);
      setPinDialogOpen(true);
      setPinCode("");
      setPinError("");
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "TND",
    }).format(amount);
  };

  const handlePinAuthentication = () => {
    const correctCode = "5099";

    // Reset error state
    setPinError("");

    // Check if the entered PIN matches the correct code
    if (pinCode !== correctCode) {
      setPinError(t("repair.invalidConfirmCode"));
      return;
    }

    // Authentication successful (only for this page visit)
    setIsAuthenticated(true);
    setPinDialogOpen(false);
    toast.success(t("repair.authenticationSuccessful"));
  };

  const handleConfirmPayment = async () => {
    if (!selectedRepair) return;

    try {
      const updates: Partial<RepairItem> = {
        paymentConfirmed: true,
        paymentConfirmedAt: new Date(),
      };

      await updateRepair(selectedRepair.id, updates);
      toast.success(t("repair.paymentConfirmed"));
      setConfirmDialogOpen(false);
      setSelectedRepair(null);
    } catch (error) {
      console.error("Error confirming payment:", error);
      toast.error(t("repair.updateError"));
    }
  };

  const openConfirmDialog = (repair: RepairItem) => {
    setSelectedRepair(repair);
    setConfirmDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Show PIN dialog first if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        {/* PIN Authentication Dialog */}
        <Dialog open={pinDialogOpen} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                {t("repair.authenticationRequired")}
              </DialogTitle>
              <DialogDescription>
                {t("repair.enterPinToAccess")}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="pin-code" className="mb-2 block">
                {t("repair.enterConfirmCode")}:
              </Label>
              <Input
                id="pin-code"
                type="password"
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
                placeholder="******"
                className="mb-2"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePinAuthentication();
                  }
                }}
              />
              {pinError && (
                <p className="text-sm text-red-500 mt-1">{pinError}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => navigate(-1)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="default"
                onClick={handlePinAuthentication}
                disabled={!pinCode}
              >
                {t("repair.authenticate")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <h1 className="text-2xl font-bold">
          {t("repair.paymentConfirmations")}
        </h1>

        <form onSubmit={handleSearch} className="relative w-full md:w-80">
          <div className="relative">
            <Input
              placeholder={t("repair.searchPaymentsPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10 w-full"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </form>
      </div>

      {filteredRepairs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRepairs.map((repair) => (
            <Card key={repair.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">
                      {repair.customerName}
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      {repair.customerPhone}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      {t("repair.payment.paid")}
                    </Badge>
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {t("repair.needsConfirmation")}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t("repair.phoneModel")}:
                    </span>
                    <span className="text-sm">{repair.phoneModel}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t("repair.repairPrice")}:
                    </span>
                    <span className="text-sm">
                      {formatCurrency(
                        repair.repairPrice +
                          (repair.priceModifications?.reduce(
                            (sum, mod) => sum + mod.amount,
                            0
                          ) || 0)
                      )}
                    </span>
                  </div>

                  {repair.paidAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {t("repair.paidAt")}:
                      </span>
                      <span className="text-sm">
                        {format(new Date(repair.paidAt), "dd/MM/yyyy - HH:mm")}
                      </span>
                    </div>
                  )}

                  {repair.ticketNumber && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {t("repair.ticketNumber")}:
                      </span>
                      <span className="text-sm">{repair.ticketNumber}</span>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="pt-2 flex justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/repair/${repair.id}`)}
                >
                  {t("repair.viewDetails")}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => openConfirmDialog(repair)}
                >
                  {t("repair.confirmPayment")}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">{t("repair.noPaymentsToConfirm")}</p>
        </div>
      )}

      {/* Simple Payment Confirmation Dialog (no PIN required after authentication) */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("repair.confirmPaymentTitle")}</DialogTitle>
            <DialogDescription>
              {selectedRepair && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">
                        {t("repair.customer")}:
                      </span>
                      <span>{selectedRepair.customerName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">
                        {t("repair.phoneModel")}:
                      </span>
                      <span>{selectedRepair.phoneModel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">{t("repair.amount")}:</span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(
                          selectedRepair.repairPrice +
                            (selectedRepair.priceModifications?.reduce(
                              (sum, mod) => sum + mod.amount,
                              0
                            ) || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <p className="mt-4 text-sm text-gray-600">
                {t("repair.confirmPaymentSimpleDescription")}
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedRepair(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmPayment}
              className="bg-green-600 hover:bg-green-700"
            >
              {t("repair.confirmPayment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentConfirmations;
